import { Injectable, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import type { Observable } from 'rxjs'
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  from,
  fromEvent,
  map,
  merge,
  of,
  startWith,
  switchMap,
  timer,
} from 'rxjs'

import { isWebkit } from '@app/utils'
import { isAndroidFirefox } from '@app/utils/android.util'

type MediaType = keyof (typeof RecorderService)['MIME_TYPES_MAP']
export type RecordingType = keyof (typeof RecorderService)['CONFIG_MAP']

interface Config {
  type: MediaType
  permissions: PermissionName[]
  constraints: {
    stream: MediaStreamConstraints
    track: {
      video?: MediaTrackConstraintSet[]
      audio?: MediaTrackConstraintSet[]
    }
  }
}

@Injectable({ providedIn: 'root' })
export class RecorderService {
  private static readonly MIME_TYPES_MAP = {
    audio: ['audio/webm;codecs=opus', 'audio/mp4'],
    video: ['video/mp4', 'video/webm'],
  } as const satisfies Record<string, string[]>

  private static readonly MIME_TYPE_MAP = () =>
    ({
      audio: RecorderService.MIME_TYPES_MAP.audio.find(MediaRecorder.isTypeSupported),
      video: RecorderService.MIME_TYPES_MAP.video.find(MediaRecorder.isTypeSupported),
    }) as const satisfies Record<MediaType, string | undefined>

  private readonly firefoxPermission = signal<PermissionState>('prompt')

  isSupported(type: MediaType): boolean {
    return (
      navigator.permissions &&
      typeof navigator?.mediaDevices?.getUserMedia === 'function' &&
      typeof MediaRecorder !== 'undefined' &&
      RecorderService.MIME_TYPE_MAP()[type] !== undefined
    )
  }

  private static readonly CONFIG_MAP = {
    verification_video: {
      type: 'video',
      permissions: ['microphone', 'camera'],
      constraints: {
        stream: { audio: true, video: true },
        track: {
          video: [{ aspectRatio: 0.7 }],
        },
      },
    },

    voice_message: {
      type: 'audio',
      permissions: ['microphone'],
      constraints: {
        stream: { audio: true },
        track: {
          audio: [],
        },
      },
    },
  } as const satisfies Record<string, Config>

  permissionState(type: RecordingType): Observable<PermissionState> {
    if (!this.isSupported(RecorderService.CONFIG_MAP[type].type)) {
      return of('denied')
    }

    return combineLatest(this.getPermissionState(type)).pipe(
      map(states => this.combinePermissionStates(states)),
      distinctUntilChanged(),
    )
  }

  requestPermissions(type: RecordingType): void {
    void (async () => {
      try {
        const stream = await this.requestMediaStream(type)
        stream.getTracks().forEach(track => track.stop())
      } catch {}
    })()
  }

  async requestMediaStream(type: RecordingType): Promise<MediaStream> {
    const stream = await navigator.mediaDevices.getUserMedia(RecorderService.CONFIG_MAP[type].constraints.stream)

    if (isAndroidFirefox()) {
      this.firefoxPermission.set('granted')
    }

    Object.entries(RecorderService.CONFIG_MAP[type].constraints.track).forEach(([kind, constraints]) => {
      stream
        .getTracks()
        .filter(track => track.kind === kind)
        .forEach(track => track.applyConstraints({ advanced: constraints }))
    })

    return stream
  }

  static getMediaMimeType(type: RecordingType) {
    return RecorderService.MIME_TYPE_MAP()[RecorderService.CONFIG_MAP[type].type]
  }

  async requestMediaRecorder(type: RecordingType, stream: MediaStream): Promise<MediaRecorder> {
    return new MediaRecorder(stream, {
      mimeType: RecorderService.getMediaMimeType(type),
    })
  }

  private combinePermissionStates(states: PermissionState[]): PermissionState {
    if (states.includes('denied')) {
      return 'denied'
    }

    if (states.every(state => state === 'granted')) {
      return 'granted'
    }

    return 'prompt'
  }

  private getPermissionState(type: RecordingType): Observable<PermissionState>[] {
    if (isAndroidFirefox()) {
      return [toObservable(this.firefoxPermission)] // Firefox does not store granted permission
    }

    return RecorderService.CONFIG_MAP[type].permissions.map(name =>
      from(navigator.permissions.query({ name })).pipe(
        switchMap(status =>
          merge(
            fromEvent(status, 'change').pipe(
              startWith(0),
              map(() => status.state),
            ),
            this.permissionStatePolling(name), // Only for safari?
          ),
        ),
      ),
    )
  }

  private permissionStatePolling(name: PermissionName): Observable<PermissionState> {
    return isWebkit()
      ? timer(0, 100).pipe(
          switchMap(() => navigator.permissions.query({ name })),
          map(status => status.state),
        )
      : EMPTY
  }
}
